import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FileManagerRef } from 'devextreme-react/file-manager';
import Button from 'devextreme-react/button';
import CheckBox from 'devextreme-react/check-box';
import Popup from 'devextreme-react/popup';
import TextBox from 'devextreme-react/text-box';
import {
  readColumnChooserPopupSize,
  writeColumnChooserPopupSize
} from './fileBrowserColumnChooserPopupState';
import { getFileManagerDetailsDataGrid } from './fileBrowserDetailsGrid';
import {
  getChooserColumnsInDisplayOrder,
  moveChooserColumn,
  setChooserColumnVisible
} from './fileBrowserGridState';
import './FileBrowserColumnChooserPopup.css';

type FileBrowserColumnChooserPopupProps = {
  visible: boolean;
  onClose: () => void;
  fileManagerRef: React.RefObject<FileManagerRef | null>;
};

export function FileBrowserColumnChooserPopup({
  visible,
  onClose,
  fileManagerRef
}: FileBrowserColumnChooserPopupProps) {
  const [search, setSearch] = useState('');
  const [revision, setRevision] = useState(0);
  const [popupSize, setPopupSize] = useState(readColumnChooserPopupSize);

  useEffect(() => {
    if (visible) {
      setSearch('');
      setRevision((value) => value + 1);
    }
  }, [visible]);

  const grid = visible ? getFileManagerDetailsDataGrid(fileManagerRef) : null;

  const items = useMemo(() => {
    if (!grid) return [];

    const query = search.trim().toLowerCase();
    const ordered = getChooserColumnsInDisplayOrder(grid);
    const visibleItems = ordered.filter((column) => column.visible);
    const hiddenItems = ordered.filter((column) => !column.visible);

    return [...visibleItems, ...hiddenItems].filter((column) => {
      return !query || column.caption.toLowerCase().includes(query);
    });
  }, [grid, search, revision]);

  const visibleOrder = useMemo(
    () => items.filter((item) => item.visible).map((item) => item.dataField),
    [items]
  );

  const bumpRevision = useCallback(() => {
    setRevision((current) => current + 1);
  }, []);

  const handleToggle = useCallback(
    (dataField: string, value: boolean) => {
      const activeGrid = getFileManagerDetailsDataGrid(fileManagerRef);
      if (!activeGrid) return;
      setChooserColumnVisible(activeGrid, dataField, value);
      bumpRevision();
    },
    [bumpRevision, fileManagerRef]
  );

  const handleMove = useCallback(
    (dataField: string, direction: -1 | 1) => {
      const activeGrid = getFileManagerDetailsDataGrid(fileManagerRef);
      if (!activeGrid) return;
      moveChooserColumn(activeGrid, dataField, direction);
      bumpRevision();
    },
    [bumpRevision, fileManagerRef]
  );

  const handleResizeEnd = useCallback((event: { width?: number; height?: number }) => {
    const width = Number(event.width);
    const height = Number(event.height);
    if (!Number.isFinite(width) || !Number.isFinite(height)) return;
    const next = { width, height };
    setPopupSize(next);
    writeColumnChooserPopupSize(next);
  }, []);

  return (
    <Popup
      visible={visible}
      onHiding={onClose}
      title="Column Chooser"
      showCloseButton
      shading={false}
      dragEnabled
      resizeEnabled
      width={popupSize.width}
      height={popupSize.height}
      hideOnOutsideClick
      onResizeEnd={handleResizeEnd}
      wrapperAttr={{ class: 'jarvis-file-browser-column-chooser-popup' }}
      position={{ my: 'center', at: 'center', of: window }}
    >
      <div className="jarvis-file-browser-column-chooser">
        <TextBox
          value={search}
          mode="search"
          placeholder="Search"
          valueChangeEvent="input"
          onValueChanged={(e) => setSearch(String(e.value ?? ''))}
        />
        <div className="jarvis-file-browser-column-chooser-items">
          {items.map((item) => {
            const visibleIndex = visibleOrder.indexOf(item.dataField);
            const canMoveUp = item.visible && visibleIndex > 0;
            const canMoveDown = item.visible && visibleIndex >= 0 && visibleIndex < visibleOrder.length - 1;

            return (
              <div key={item.dataField} className="jarvis-file-browser-column-chooser-row">
                <div className="jarvis-file-browser-column-chooser-reorder">
                  <Button
                    icon="chevronup"
                    stylingMode="text"
                    hint="Move up"
                    disabled={!canMoveUp}
                    onClick={() => handleMove(item.dataField, -1)}
                  />
                  <Button
                    icon="chevrondown"
                    stylingMode="text"
                    hint="Move down"
                    disabled={!canMoveDown}
                    onClick={() => handleMove(item.dataField, 1)}
                  />
                </div>
                <CheckBox
                  text={item.caption}
                  value={item.visible}
                  disabled={item.required}
                  onValueChanged={(e) => handleToggle(item.dataField, !!e.value)}
                />
              </div>
            );
          })}
        </div>
      </div>
    </Popup>
  );
}
